import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { api } from '@/services/api';
import { usePageTitle } from '@/shared/usePageTitle';
import { useToast } from '@/shared/toast';
import { PageHeader } from '@/shared/PageHeader';
import { FolderBrowser } from './FolderBrowser';
import { DocumentCard } from './DocumentCard';
import {
  useDocumentActions,
  isInNativeWrapper,
  UPLOAD_ACCEPT,
  MAX_UPLOAD_MB,
  type DocumentItem,
} from './useDocumentActions';
import { requestNativePhotoUpload, onNativeDocumentUploaded } from '@/services/nativeBridge';

// Etichette tab con icone come legacy documenti.html.
const CATEGORY_TABS = [
  { key: 'ODG', label: '📋 ODG' },
  { key: 'POS', label: '🛡️ POS' },
  { key: 'Manuali', label: '📚 Manuali' },
  { key: 'Rifornimenti', label: '⛽ Rifornimenti' },
] as const;

type Category = (typeof CATEGORY_TABS)[number]['key'];

export function DocumentsPage(): React.JSX.Element {
  usePageTitle('Documenti');
  const toast = useToast();
  const qc = useQueryClient();
  const [category, setCategory] = useState<Category>('ODG');
  // Nome custom opzionale per lo scontrino Rifornimenti (es. "ranger 22/10/2026").
  const [customName, setCustomName] = useState('');
  // Modale "dai un nome" mostrata al momento del caricamento di un Rifornimento.
  const [showNameModal, setShowNameModal] = useState(false);
  // Stato visivo dragover dell'upload area (verde come legacy).
  const [dragOver, setDragOver] = useState(false);

  const isRifornimenti = category === 'Rifornimenti';

  // Rifornimenti = buffer piatto (nessuna cartella): folderId sempre null.
  const { uploading, uploadFiles, openDocument, deleteDocument } =
    useDocumentActions({ category, folderId: null });

  // Lista Rifornimenti (root della categoria). Per ODG/POS/Manuali la lista
  // vive in FolderBrowser (per livello), quindi qui la query resta disabilitata.
  const { data } = useQuery<DocumentItem[]>({
    queryKey: ['documents', category],
    queryFn: async () => (await api.get('/documents', { params: { category } })).data,
    enabled: isRifornimenti,
  });

  // App nativa: l'upload lo fa il wrapper (picker + fetch nativi). A fine upload
  // emette l'evento DocumentUploaded -> ricarichiamo liste file + cartelle.
  useEffect(() => {
    return onNativeDocumentUploaded(() => {
      qc.invalidateQueries({ queryKey: ['documents'] });
      qc.invalidateQueries({ queryKey: ['doc-folders'] });
      toast.show('Documento caricato');
    });
  }, [qc, toast]);

  // Click sull'area upload Rifornimenti: prima chiedo il nome (modale), poi procedo.
  function startUpload(): void {
    setCustomName('');
    setShowNameModal(true);
  }

  // Confermato il nome nella modale: scatta/sceglie la foto (app) o apre il
  // selettore file (browser). customName resta in stato e viene usato nell'upload.
  function proceedRifornimentiUpload(): void {
    setShowNameModal(false);
    if (isInNativeWrapper()) {
      requestNativePhotoUpload(category, customName.trim() || undefined);
    } else {
      document.getElementById('doc-upload')?.click();
    }
  }

  async function upload(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    // Rifornimenti: un solo file per volta (nome custom) + tetto 20 lato server.
    await uploadFiles(Array.from(e.target.files ?? []), { rename: customName, limit: 1 });
    setCustomName('');
    e.target.value = '';
  }

  return (
    <div className="page-container">
      <PageHeader title="📄 Documenti" />

      <div className="toolbar">
        {CATEGORY_TABS.map(c => (
          <button key={c.key} onClick={() => setCategory(c.key)}
            className={category === c.key ? 'btn-success' : 'btn-secondary'}>
            {c.label}
          </button>
        ))}
      </div>

      {isRifornimenti ? (
        <>
          {/* Modale "dai un nome" al momento del caricamento di un Rifornimento. */}
          {showNameModal && (
            <div className="modal-overlay" onClick={() => setShowNameModal(false)}>
              <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
                <h3 style={{ marginTop: 0, marginBottom: 12 }}>⛽ Nuovo scontrino</h3>
                <p style={{ marginTop: 0, color: '#666', fontSize: 14 }}>
                  Dai un nome al file (facoltativo), poi scegli/scatta la foto.
                </p>
                <input
                  type="text"
                  autoFocus
                  value={customName}
                  onChange={e => setCustomName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') proceedRifornimentiUpload(); }}
                  placeholder="es. ranger 22/10/2026"
                  style={{ width: '100%', padding: 10, margin: '4px 0 16px',
                           border: '1px solid #ced4da', borderRadius: 6, boxSizing: 'border-box' }}
                />
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button onClick={() => setShowNameModal(false)} className="btn-secondary"
                    style={{ padding: '8px 16px' }}>Annulla</button>
                  <button onClick={proceedRifornimentiUpload} className="btn-success"
                    style={{ padding: '8px 16px' }}>Scegli foto →</button>
                </div>
              </div>
            </div>
          )}

          {/* Upload area click + drag&drop, replica del legacy documenti.html. */}
          {/* Input file FUORI dalla .upload-area: il click() programmatico
              (proceedRifornimentiUpload) NON deve bubblare su onClick={startUpload},
              altrimenti startUpload azzera customName e il nome dello scontrino
              digitato va perso. Bug web-only (il nativo usa il bridge, non input.click()). */}
          <input id="doc-upload" type="file" onChange={upload} disabled={uploading}
            accept={UPLOAD_ACCEPT} style={{ display: 'none' }} />
          <div className={`upload-area${dragOver ? ' dragover' : ''}`}
            onClick={startUpload}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => {
              e.preventDefault();
              setDragOver(false);
              void uploadFiles(Array.from(e.dataTransfer.files ?? []), { rename: customName, limit: 1 });
            }}>
            <div style={{ fontSize: 48, marginBottom: 15 }}>📤</div>
            <h3 style={{ marginBottom: 10 }}>Carica documento {category}</h3>
            {uploading ? (
              <p style={{ color: '#666', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <span className="upload-spinner" />
                Caricamento in corso...
              </p>
            ) : (
              <p style={{ color: '#666' }}>
                Clicca o trascina un file (PDF, Word, Excel, TXT, CSV, Immagini — max {MAX_UPLOAD_MB} MB)
              </p>
            )}
          </div>

          <div className="doc-grid">
            {(data ?? []).map(d => (
              <DocumentCard key={d.id} doc={d} onOpen={openDocument} onDelete={deleteDocument} />
            ))}
            {(data ?? []).length === 0 && (
              <div className="empty-state" style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: 48, marginBottom: 10 }}>📁</div>
                Nessun documento in questa categoria
              </div>
            )}
          </div>
        </>
      ) : (
        <FolderBrowser category={category} />
      )}
    </div>
  );
}
